import { Layout, Layouts } from "react-grid-layout";
import {
  baseWidgetDetails,
  ICols,
  IWidgetDetails,
} from "@/lib/widgetsLayoutDir";

// LQC-2517: RGL only clamps to minW/minH while a resize is in flight - never on the
// layout it is handed. Workspaces are persisted per user, so a widget saved before its
// mins existed keeps no mins forever and can be dragged down to a bare header. The
// canonical mins from baseWidgetDetails are therefore re-applied on every load.

// Components with no baseWidgetDetails entry (the hidden "FirstLoad" placeholder) are
// left untouched.
export function returnMinSizeForComponent(component: string | undefined) {
  const details = baseWidgetDetails.find(
    (widget) => widget.component == component,
  );
  if (!details) return undefined;
  return { minW: details.minW, minH: details.minH };
}

// A min wider than the breakpoint has columns would pin the widget off-grid, so cap it.
export function applyMinSizeToLayoutItem(
  item: Layout,
  component: string | undefined,
  colsForBreakpoint: number,
): Layout {
  const minSize = returnMinSizeForComponent(component);
  if (!minSize) return item;

  const minW = Math.min(minSize.minW, colsForBreakpoint);
  const minH = minSize.minH;
  const w = Math.max(item.w, minW);
  const h = Math.max(item.h, minH);

  const unchanged =
    item.minW == minW && item.minH == minH && item.w == w && item.h == h;
  if (unchanged) return item;

  return { ...item, minW, minH, w, h };
}

// Returns the same references when nothing changed - a new object every render would
// churn RGL and re-trigger onLayoutChange (which saves to the DB).
export function applyMinSizesToLayouts(
  layouts: Layouts,
  openWidgets: IWidgetDetails[],
  cols: ICols,
): Layouts {
  const result: Layouts = {};
  let anyBreakpointChanged = false;

  for (const breakpoint of Object.keys(layouts)) {
    let breakpointChanged = false;

    const normalised = layouts[breakpoint].map((item) => {
      const component = openWidgets.find(
        (widget) => widget.i == item.i,
      )?.component;
      const next = applyMinSizeToLayoutItem(
        item,
        component,
        cols[breakpoint] ?? 12,
      );
      if (next !== item) breakpointChanged = true;
      return next;
    });

    result[breakpoint] = breakpointChanged ? normalised : layouts[breakpoint];
    if (breakpointChanged) anyBreakpointChanged = true;
  }

  return anyBreakpointChanged ? result : layouts;
}
